import { useNavigate, useParams } from "react-router-dom";
import { Avatar, ButtonBase, Card, Chip, Divider, Stack, Typography } from "@mui/material";
import { format } from "date-fns";
import Price from "@/components/Price";
import routes from "@/constants/routes";
import { DINING_MODES, MENU_FLOW_LABELS, ORDER_SOURCE_LABELS } from "@/constants/global";
import { getOrderTotal } from "@/utils/order";
import { getInitials, stringToColor } from "@/utils/names";
import { Order, OrderStatus, PaymentMethodsType } from "@/types";
import useOrganizations from "@/features/organizations/useOrganizations";
import { orange, purple } from "@mui/material/colors";

const OrderCard = ({ createdAt, orderedAt, items = [], number, id, flow, client, source, deliveryLocation, deliveryFee = 0, status, paymentDetails, paidAt }: Order) => {
  const navigate = useNavigate();
  const { organizationId } = useParams<{ organizationId: string }>();
  const { currentOrganization: organization } = useOrganizations();
  const stripeEnabled = Boolean(organization?.settings?.paymentMethods?.stripeEnabled);
  const isWaitingPayment =
    status === OrderStatus.PENDING_PAYMENT ||
    (stripeEnabled &&
      paymentDetails?.method === PaymentMethodsType.CREDIT_CARD &&
      paymentDetails?.status !== "paid" &&
      !paidAt &&
      status === OrderStatus.PREPARING);

  const openOrderDetail = () => {
    navigate(routes.organizations.orders.details.replace(":organizationId", organizationId ?? "").replace(":orderId", id));
  };

  const dineModeColor = DINING_MODES.find((m) => String(m.id) === String(flow))?.color[500];

  return (
    <ButtonBase component="div" onClick={openOrderDetail} sx={{ width: "100%" }}>
      <Card sx={{ backgroundColor: "#DDEAFD", width: "100%" }}>
        <Stack py={1}>
          <Stack p={2} spacing={2} direction="row" alignItems="center" justifyContent="">
            <Avatar sx={{ backgroundColor: stringToColor(client?.name + " " + client?.lastname) }}>{getInitials(client?.name + " " + client?.lastname)}</Avatar>
            <Stack alignItems="flex-start">
              <Typography variant="h4">{client?.name + " " + client?.lastname} </Typography>
              <Typography
                color="white"
                sx={{
                  bgcolor: flow ? dineModeColor : "#9E9E9E",
                  borderRadius: 1,
                  px: 1,
                }}
              >
                {flow ? MENU_FLOW_LABELS[flow as unknown as keyof typeof MENU_FLOW_LABELS] : "n/a"}
              </Typography>
            </Stack>
          </Stack>
          <Divider />

          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack flex={1} p={1.5} direction="row" spacing={2} justifyContent={"space-between"}>
              <Typography>No.</Typography>
              <Typography variant="body2">{number}</Typography>
            </Stack>
            <Divider orientation="vertical" flexItem />
            <Stack flex={1} p={1.5} spacing={1}>
              {orderedAt && typeof orderedAt === "number" ? (
                <Stack direction="row" spacing={2} justifyContent={"space-between"}>
                  <Typography>Hora</Typography>
                  <Typography variant="body2">{format(orderedAt, "h:mma")}</Typography>
                </Stack>
              ) : createdAt && typeof createdAt === "number" ? (
                <Stack direction="row" spacing={2} justifyContent={"space-between"}>
                  <Typography>Hora</Typography>
                  <Typography variant="body2">{format(createdAt, "h:mma")}</Typography>
                </Stack>
              ) : null}
            </Stack>
          </Stack>
          <Divider />

          <Stack direction="row" alignItems="center" justifyContent="space-between">
            {deliveryLocation?.distance?.text ? (
              <>
                <Stack flex={1} p={1.5} direction="row" spacing={2} justifyContent={"space-between"}>
                  <Typography>Dist.</Typography>
                  <Typography variant="body2">{deliveryLocation.distance.text}</Typography>
                </Stack>
                <Divider />
              </>
            ) : null}
            <Divider orientation="vertical" flexItem />
            {deliveryLocation?.duration?.text ? (
              <>
                <Stack flex={1} p={1.5} direction="row" spacing={2} justifyContent={"space-between"}>
                  <Typography>Tiempo</Typography>
                  <Typography variant="body2">{deliveryLocation.duration.text}</Typography>
                </Stack>
                <Divider />
              </>
            ) : null}
          </Stack>
          <Divider />

          <Stack p={1.5} direction="row" spacing={2} justifyContent={"space-between"}>
            <Typography>Fuente</Typography>
            <Typography variant="body2">{ORDER_SOURCE_LABELS[source as keyof typeof ORDER_SOURCE_LABELS] ?? "N/A"}</Typography>
          </Stack>
          <Divider />

          <Stack p={1.5} direction="row" spacing={2} justifyContent={"space-between"}>
            <Typography>Total</Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              {isWaitingPayment ? (
                <Chip label="esperando pago" size="small" sx={{ backgroundColor: orange[700], color: "white" }} />
              ) : (
                <Chip label="nuevo" size="small" sx={{ backgroundColor: purple[500], color: "white" }} />
              )}
              {items ? (
                <Typography variant="body2">
                  <Price currency="NIO">{getOrderTotal(items, organization?.settings?.includeTax, Number(deliveryFee))}</Price>
                </Typography>
              ) : null}
            </Stack>
          </Stack>
        </Stack>
      </Card>
    </ButtonBase>
  );
};

export default OrderCard;
